import moment from 'moment';
import { addDays, isAfter, isBefore, isEqual, isWithinInterval, parseISO } from 'date-fns';
import type {
  CarrierCarrierInfo,
  CarrierServiceMethodInfo,
  PartnerAveragePackageDimensions,
  PartnerCarrierRateCreate,
  PartnerDimWeightOverride,
  PartnerRateCalendarEntry,
  PartnerRateShoppingCostModifier,
  PartnerSurchargeInfo,
} from '@shipium-corp/shipium-ts-types';
import { arrayToMapTransformed } from '~/hooks/useQueryCreator';
import type {
  CarrierRateCalendarEntry,
  CustomShoppingCostModifierType,
  SurchargeInfoFormData,
  SurchargeInfoWithBands,
  SurchargeModifierBand,
} from './rateCardTypes';
import { getCurrencySymbol, type CurrencyCode } from '~/store/i18n/currencyUtils';
import { capitalizeWords } from '~/utils/regex';
import type { PartnerCarrierRateSummaryEntry } from '~/services/partner-management-service';

export const PERCENT_VALUE_MODIFIERS = ['percentage', 'replacement_percentage', 'fixed_percentage_discount'];
export const FIXED_VALUE_MODIFIERS = ['credit', 'replacement'];

/** Converts an API decimal percentage (e.g. 0.55) to a display percentage (e.g. 55), avoiding floating-point drift. */
export const apiPercentageToDisplay = (val: number) => Math.round(val * 10000) / 100;

/**
 * A modifier value of `0` is meaningful — it is how a full surcharge waiver is stored
 * (e.g. a `replacement` of 0, or a `replacement_percentage` of 0%). Falsey checks read
 * that as "nothing entered", which blocks saves on configs that already carry zeros, so
 * presence has to be tested explicitly. Values arrive as numbers from the API and as
 * strings from `<Input type="number" />`, which yields `''` when cleared.
 */
export const hasModifierValue = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

export const modifyApiPercentageForDisplay = (val: number, modifierType?: string) => {
  if (modifierType && PERCENT_VALUE_MODIFIERS.includes(modifierType)) {
    return Math.round((val + Number.EPSILON) * 10000) / 100;
  }
  return val;
};

/** The form holds percentages as whole numbers; the API takes fractions. */
const toApiModifierValue = (modifierType: string | undefined, modifierValue: number | undefined) =>
  PERCENT_VALUE_MODIFIERS.includes(modifierType!) ? modifierValue! / 100 : modifierValue;

/** Moves each band's percentage from the form's whole-number scale to the API's fractions. */
export const bandsToApi = (bands: SurchargeModifierBand[]): SurchargeModifierBand[] =>
  bands.map((band) => ({ ...band, modifierValue: toApiModifierValue(band.modifierType, band.modifierValue) }));

/**
 * Inverse of {@link bandsToApi}. PMS serializes an open bound (and an unset unit) as `null`; it is
 * normalized to undefined here so the rest of the console sees the generated type's shape. PMS reads
 * an omitted bound as open, so sending it back omitted is equivalent.
 */
export const bandsToDisplay = (bands: SurchargeModifierBand[]): SurchargeModifierBand[] =>
  bands.map((band) => ({
    ...band,
    lowerBound: band.lowerBound ?? undefined,
    upperBound: band.upperBound ?? undefined,
    weightUnit: band.weightUnit ?? undefined,
    modifierValue:
      band.modifierValue === undefined
        ? band.modifierValue
        : modifyApiPercentageForDisplay(band.modifierValue, band.modifierType ?? ''),
  }));

/**
 * @deprecated
 */
export const shapeNegotiatedSurcharges = (negotiatedSurcharges: SurchargeInfoFormData): SurchargeInfoWithBands[] => {
  return (
    Object.entries(negotiatedSurcharges ?? {})
      .map(([surchargeId, info]) => {
        const { modifierType, modifierValue, modifierBands } = info;

        // The band matching the package weight supplies the pair applied. The scalar pair
        // travels with it as the fallback for consumers that predate bands, which is how
        // partner-management-service documents it; it is a primitive float there, so an
        // omitted value reads as 0 rather than as absent.
        if (modifierBands?.length) {
          return {
            surchargeId,
            ...info,
            modifierValue: toApiModifierValue(modifierType, modifierValue),
            modifierType,
            modifierBands: bandsToApi(modifierBands),
          };
        }

        return {
          surchargeId,
          ...info,
          modifierValue: toApiModifierValue(modifierType, modifierValue),
        };
      })
      .filter((val) => val.modifierBands?.length || hasModifierValue(val.modifierValue)) ?? []
  );
};

/**
 * @deprecated
 */
export const shapeRateCalendarEntry = (payload: any): any => {
  const {
    rateCalendarEntryId,
    endDate,
    startDate,
    negotiatedSurcharges,
    rates,
    weightUnit,
    cubicRates,
    cubicVolumeUnit,
    uncosted,
    rateShoppingCostModifier,
    modifierType,
    overSizedRates,
    minimumOverWeightRate,
    ratePerWeightUnit,
    shipmentMinimumBaseRate,
    currency,
    ratingType,
  } = payload;

  return {
    ...(rateCalendarEntryId && { rateCalendarEntryId }),
    endDateTime: endDate.endOf('day').utc().toISOString(),
    startDateTime: moment(startDate).startOf('day').utc().toISOString() ?? new Date().toISOString(),
    rateTable: {
      currency: ratingType !== 'UNCOSTED' ? (currency ?? 'USD') : undefined,
      rates: !uncosted ? (rates ?? {}) : {},
      weightUnit: weightUnit ?? 'lb',
      ...(!uncosted && cubicRates && { cubicRates: cubicRates }),
      ...(cubicVolumeUnit && { cubicVolumeUnit: cubicVolumeUnit }),
      ...(rateShoppingCostModifier && !uncosted
        ? {
            rateShoppingCostModifier: {
              modifierType: modifierType || 'flat',
              modifierValue: rateShoppingCostModifier,
              name: 'SHIPIUM',
              rateShoppingCostModifierId: 'SHIPIUM',
            },
          }
        : {}),
      ...(Object.values(overSizedRates ?? {}).filter(Boolean).length > 0 && {
        overSizedRate: overSizedRates,
      }),
      ...(Object.values(ratePerWeightUnit ?? {}).filter(Boolean).length > 0 && {
        ratePerWeightUnit: ratePerWeightUnit,
      }),
      ...(Object.values(minimumOverWeightRate ?? {}).filter(Boolean).length > 0 && {
        minimumOverWeightRate: minimumOverWeightRate,
      }),
      ...(shipmentMinimumBaseRate != null && { shipmentMinimumBaseRate }),
    },
    ratingType: ratingType || 'UNCOSTED',
    uncosted: !ratingType || ratingType === 'UNCOSTED',
    negotiatedSurcharges: negotiatedSurcharges ? shapeNegotiatedSurcharges(negotiatedSurcharges) : [],
  } as PartnerRateCalendarEntry;
};

// The dimWeightOverride keys the DIM weight form has inputs for. Every other key on a saved override
// is invisible to the form, and an update replaces the whole override, so those keys are carried
// over from the saved rate. The deprecated volumeWeightDivisor is listed so it is never resent next
// to the tiers that replaced it.
const FORM_DIM_WEIGHT_OVERRIDE_KEYS: ReadonlySet<string> = new Set([
  'volumeThreshold',
  'cubicVolumeUnit',
  'discountFactors',
  'volumeWeightDivisorTiers',
  'volumeWeightDivisor',
]);

// Only Shipium users get inputs for these, so for anyone else they are carried over like any other
// key the form does not show.
const WEIGHT_THRESHOLD_KEYS: ReadonlySet<string> = new Set(['weightThreshold', 'thresholdMatch']);

export const mergeDimWeightOverride = (
  saved: PartnerDimWeightOverride | undefined,
  formValue: PartnerDimWeightOverride | undefined,
  showsWeightThreshold: boolean,
): PartnerDimWeightOverride | undefined => {
  const isFormKey = (key: string) =>
    FORM_DIM_WEIGHT_OVERRIDE_KEYS.has(key) || (showsWeightThreshold && WEIGHT_THRESHOLD_KEYS.has(key));
  const preserved = Object.fromEntries(Object.entries(saved ?? {}).filter(([key]) => !isFormKey(key)));
  if (Object.keys(preserved).length === 0) {
    return formValue;
  }
  return { ...preserved, ...formValue } as PartnerDimWeightOverride;
};

/**
 * @deprecated
 */
export const shapeCarrierRate = (payload: any): PartnerCarrierRateCreate => {
  const {
    name,
    carrierId,
    carrierRateSetId,
    partnerProvidedId,
    description,
    serviceMethod,
    destinationType,
    negotiatedDimweightDivisor,
    negotiatedDimweightThreshold,
    dimweightCubicVolumeUnit = 'in', //this is required
    discountFactors,
    volumeWeightDivisorTiers,
    endDate,
    averagePackageVolume,
    averagePackageVolumeUnit,
    carrierRateType,
  } = payload;
  return {
    ...(carrierRateSetId && { carrierRateSetId }),
    carrierRateType: carrierRateType ?? 'contract',
    carrier: carrierId,
    name,
    ...(partnerProvidedId && { partnerProvidedId }),
    description: description || '',
    carrierServiceMethodId: serviceMethod,
    destinationType: destinationType!, //TODO: find out what happens if this is undefined
    ...((averagePackageVolume
      ? {
          averagePackageDimensions: {
            averagePackageVolume,
            linearUnit: averagePackageVolumeUnit,
          },
        }
      : {}) as PartnerAveragePackageDimensions),
    dimWeightOverride: {
      volumeThreshold: negotiatedDimweightThreshold,
      cubicVolumeUnit: dimweightCubicVolumeUnit,
      discountFactors,
      volumeWeightDivisorTiers,
      volumeWeightDivisor: negotiatedDimweightDivisor,
    },
    ...(endDate && { rateCalendar: [shapeRateCalendarEntry(payload)] }),
  };
};

export const customShoppingCostModifierOptions = [
  'percentage',
  'flat',
  'percentage-discount',
  'flat-discount',
] as CustomShoppingCostModifierType[];

export const getCustomShoppingCostModifierLabel = (modifierType: CustomShoppingCostModifierType) => {
  switch (modifierType) {
    case 'percentage':
      return 'Percentage Cost Increase';
    case 'flat':
      return 'Flat Rate Cost Increase';
    case 'percentage-discount':
      return 'Percentage Discount';
    case 'flat-discount':
      return 'Flat Rate Discount';
  }
};

export const getCustomShoppingCostModifier = (
  modifierType: PartnerRateShoppingCostModifier['modifierType'] | undefined,
  value: number | null | undefined,
): { modifierType: CustomShoppingCostModifierType | undefined; modifierValue: number | undefined } => {
  if (typeof value === 'number' && value < 0) {
    if (modifierType === 'flat') {
      return { modifierType: 'flat-discount', modifierValue: value * -1 };
    }

    if (modifierType === 'percentage') {
      return { modifierType: 'percentage-discount', modifierValue: value * -1 };
    }
  }

  return { modifierType, modifierValue: value ?? undefined };
};

export const isCustomShoppingCostPercentageModifier = (modifierType: CustomShoppingCostModifierType) => {
  return modifierType === 'percentage' || modifierType === 'percentage-discount';
};

export const getShoppingCostModifier = (
  modifierType: CustomShoppingCostModifierType | undefined,
  value: number | null | undefined,
): { modifierType: PartnerRateShoppingCostModifier['modifierType']; modifierValue: number | undefined } | undefined => {
  // Omit if value is falsy or zero
  if (value) {
    if (modifierType === 'flat-discount') {
      return { modifierType: 'flat', modifierValue: Math.abs(value) * -1 };
    }

    if (modifierType === 'percentage-discount') {
      return { modifierType: 'percentage', modifierValue: Math.abs(value) * -1 };
    }

    if (modifierType === 'percentage') {
      return { modifierType: 'percentage', modifierValue: Math.abs(value) };
    }

    return { modifierType: 'flat', modifierValue: Math.abs(value) };
  }
};

/**
 * @deprecated
 */
export const shapeNegotiatedSurchargeFormData = (negotiatedSurcharges?: PartnerSurchargeInfo[]) => {
  if (!Array.isArray(negotiatedSurcharges)) return [];
  const result: Record<string, PartnerSurchargeInfo> = {};
  for (const curr of negotiatedSurcharges) {
    if (curr.surchargeId) {
      const { modifierType } = curr;
      // Each band carries its own modifier pair, which moves to the display scale with the scalar.
      const bands = (curr as SurchargeInfoWithBands).modifierBands;
      const modifierBands = bands?.length ? bandsToDisplay(bands) : undefined;
      // A banded entry may have no scalar pair; defaulting it to 0 would read as a value with no type.
      const modifierValue =
        modifierBands && curr.modifierValue === undefined
          ? undefined
          : modifyApiPercentageForDisplay(curr.modifierValue || 0, modifierType || '');
      result[curr.surchargeId] = { ...curr, modifierValue, ...(modifierBands ? { modifierBands } : {}) };
    }
  }
  return result;
};

export const findStartDate = (
  rateCalendar: { startDateTime?: moment.Moment; endDateTime?: moment.Moment }[] | undefined,
) => {
  const datedEntries = rateCalendar?.filter((entry) => entry.endDateTime) ?? [];
  if (datedEntries.length === 0) {
    return undefined;
  }

  const latestEntry = datedEntries.reduce((latest, current) =>
    current.endDateTime!.isAfter(latest.endDateTime!) ? current : latest,
  );

  return latestEntry.endDateTime;
};

export const getWeightUnitFromRateTableConfig = (serviceMethod?: CarrierServiceMethodInfo): 'kg' | 'lb' => {
  let unit = 'lb';
  if (serviceMethod?.rateTableRangeConfiguration) {
    unit = serviceMethod.rateTableRangeConfiguration.weightUnit;
  } else {
    unit = serviceMethod?.restrictions?.weights?.[0]?.weightUnit ?? 'lb';
  }
  if (unit === 'g') {
    return 'kg';
  }
  if (unit === 'oz') {
    return 'lb';
  }
  return unit as 'kg' | 'lb';
};

// only currently used for axelhire
// slightly more scientific than hard coding "axelhire"
// looks to see if max_average_volume is present in the surcharges for carrier
// and if that surcharge applies to a given method
const AVERAGE_VOLUME_THRESHOLD_TYPE = 'max_average_volume';

export const methodHasAverageVolumeSurcharge = (
  carrier?: CarrierCarrierInfo,
  serviceMethod?: CarrierServiceMethodInfo,
): boolean => {
  const methodSurchargeSet = new Set(serviceMethod?.applicableSurchargeIds ?? []);

  return !!carrier?.orderedSurcharges
    ?.filter((surcharge) => methodSurchargeSet.has(surcharge?.carrierSurchargeId))
    ?.some((surcharge) => {
      return surcharge?.filterToThreshold?.dimensions?.some(
        (dim) => dim?.thresholdType === AVERAGE_VOLUME_THRESHOLD_TYPE,
      );
    });
};

export const getDateAsLocalTime = (date: string) => {
  // Avoid daylight savings issues
  const iso = parseISO(date);
  const actualDay = new Date(
    iso.getUTCFullYear(),
    iso.getUTCMonth(),
    iso.getUTCDate(),
    iso.getUTCHours(),
    iso.getUTCMinutes(),
    iso.getUTCSeconds(),
    iso.getUTCMilliseconds(),
  );

  const middleOfDay = new Date(actualDay.getUTCFullYear(), actualDay.getUTCMonth(), actualDay.getUTCDate(), 12);
  const expectedDay = new Date(actualDay.getUTCFullYear(), actualDay.getUTCMonth(), actualDay.getUTCDate());

  // If before or at noon, use the expected day
  if (isBefore(actualDay, middleOfDay) || isEqual(actualDay, middleOfDay)) {
    return expectedDay;
  }

  // Else use the expected day + 1
  return addDays(expectedDay, 1);
};

export const getDateAsUTCTime = (moment: moment.Moment, second: 0 | -1 = 0) => {
  return new Date(Date.UTC(moment.year(), moment.month(), moment.date(), 8, 0, second));
};

export const adjustRateCalendarFormEntries = (rateCalendar: PartnerRateCalendarEntry[]) => {
  const adjustedEntries: CarrierRateCalendarEntry[] = [];
  let lastEndDate: Date | undefined;

  rateCalendar.sort((a, b) => moment(a.startDateTime).diff(moment(b.startDateTime)));

  for (const entry of rateCalendar) {
    let startDateTime = getDateAsLocalTime(entry.startDateTime);
    if (lastEndDate && isAfter(lastEndDate, startDateTime)) {
      startDateTime = lastEndDate;
    }

    lastEndDate = getDateAsLocalTime(entry.endDateTime);

    adjustedEntries.push({
      ...entry,
      startDateTime: moment(startDateTime),
      endDateTime: moment(lastEndDate),
      additionalRateTables: arrayToMapTransformed(entry.additionalRateTables ?? [], 'qualifier', (value) => {
        return {
          ...value.rateTable,
          rateShoppingCostModifier: getCustomShoppingCostModifier(
            value.rateTable?.rateShoppingCostModifier?.modifierType,
            value.rateTable?.rateShoppingCostModifier?.modifierValue,
          ),
        };
      }),
      negotiatedSurcharges: arrayToMapTransformed(entry.negotiatedSurcharges ?? [], 'surchargeId', (value) => {
        // Bands have to survive the load, or saving an untouched banded card writes them away.
        const bands = (value as SurchargeInfoWithBands).modifierBands;
        return {
          modifierType: value.modifierType,
          modifierValue: PERCENT_VALUE_MODIFIERS.includes(value.modifierType!)
            ? apiPercentageToDisplay(value.modifierValue!)
            : value.modifierValue,
          ...(bands?.length ? { modifierBands: bandsToDisplay(bands) } : {}),
        };
      }),
      rateTable: {
        ...entry.rateTable,
        rateShoppingCostModifier: getCustomShoppingCostModifier(
          entry.rateTable?.rateShoppingCostModifier?.modifierType,
          entry.rateTable?.rateShoppingCostModifier?.modifierValue,
        ),
      },
    });
  }

  return adjustedEntries;
};

/**
 * @deprecated
 */
export const validateRateValue = (_: any, value: any) => {
  try {
    if (value === undefined || value === null) {
      // allow empty values for optional fields
      return Promise.resolve();
    }

    if (isNaN(value)) return Promise.reject('Value must be a number.');

    if (Number(value) < 0) return Promise.reject('Value must be greater than 0.');

    return Promise.resolve();
  } catch {
    return Promise.reject();
  }
};

export const SHIPIUM_COSTING = 'SHIPIUM_COSTING';
export const UNCOSTED = 'UNCOSTED';

export function getCarrierRatingOptions(supportedRatingTypes: string[]) {
  const options = [
    { value: SHIPIUM_COSTING, label: 'Shipium Costing' },
    { value: 'CARRIER_API', label: 'Carrier Rating' },
  ];

  return options
    .filter((rating) => supportedRatingTypes.includes(rating.value))
    .concat({ value: UNCOSTED, label: 'Uncosted' });
}

export const carrierRatingMap = {
  [SHIPIUM_COSTING]: 'Shipium Costing',
  [UNCOSTED]: 'Uncosted',
  CARRIER_API: 'Carrier Rating',
};

export const rateEntryKey = (rate: PartnerRateCalendarEntry) => `${rate?.startDateTime}-${rate?.endDateTime}`;

export const isStartDayDisabled = ({
  date,
  neighboringEntries,
}: {
  date: moment.Moment;
  neighboringEntries: { startDateTime?: moment.Moment; endDateTime?: moment.Moment }[];
}) => {
  for (const neighborEntry of neighboringEntries) {
    if (!neighborEntry.startDateTime || !neighborEntry.endDateTime) {
      continue;
    }
    if (
      (isAfter(date.toDate(), neighborEntry.startDateTime.toDate()) ||
        isEqual(date.toDate(), neighborEntry.startDateTime.toDate())) &&
      isBefore(date.toDate(), neighborEntry.endDateTime.toDate())
    ) {
      return true;
    }
  }

  return false;
};

export const isEndDayDisabled = ({
  date,
  neighboringEntries,
  startDate,
}: {
  date: moment.Moment;
  neighboringEntries: { startDateTime?: moment.Moment; endDateTime?: moment.Moment }[];
  startDate: moment.Moment | undefined;
}) => {
  for (const neighborEntry of neighboringEntries) {
    if (!neighborEntry.startDateTime || !neighborEntry.endDateTime) {
      continue;
    }
    const neighborEntryStartDateTime = neighborEntry.startDateTime.toDate();

    if (
      isAfter(date.toDate(), neighborEntryStartDateTime) &&
      isBefore(date.toDate(), neighborEntry.endDateTime.toDate())
    ) {
      return true;
    }

    if (
      startDate &&
      isBefore(startDate.toDate(), neighborEntryStartDateTime) &&
      isAfter(date.toDate(), neighborEntryStartDateTime)
    ) {
      return true;
    }
  }

  if (startDate) {
    return isBefore(date.toDate(), addDays(startDate.toDate(), 1));
  }

  return false;
};

export const surchargeModifierDiscountOptions = ['percentage', 'fixed_percentage_discount', 'credit'];
export const surchargeModifierReplacementOptions = ['replacement_percentage', 'replacement'];

export const getSurchargeModifierLabel = ({
  modifierType,
  currencyCode,
}: {
  modifierType: string;
  currencyCode: CurrencyCode;
}) => {
  switch (modifierType) {
    case 'replacement_percentage':
      return 'Fixed % Surcharge';
    case 'replacement':
      return `Fixed ${getCurrencySymbol(currencyCode)} Surcharge`;
    case 'percentage':
      return '% Surcharge Discount';
    case 'fixed_percentage_discount':
      return 'Fixed % Surcharge Discount';
    case 'credit':
      return `${getCurrencySymbol(currencyCode)} Surcharge Discount`;
  }
};

export const STANDARD_RATE_CARD_ENUM = 'standard_rate_sheet';
export const RURAL_RATE_CARD_ENUM = 'rural_rate_sheet';

export const getRateTableTabProps = (qualifier: string) => {
  if (qualifier === RURAL_RATE_CARD_ENUM) {
    return { label: 'Remote Rate Sheet', rgb: '180, 200, 180' };
  }

  return { label: capitalizeWords(qualifier.replaceAll('_', ' ')), rgb: '180, 180, 200' };
};

export const getActiveCalendarEntry = <T extends PartnerCarrierRateSummaryEntry | PartnerRateCalendarEntry>(
  rateCalendars: T[],
): { activeRateEntry: T | undefined; activeRateEntryIndex: number } => {
  for (const [index, entry] of rateCalendars.entries()) {
    if (
      isWithinInterval(Date.now(), {
        start: Date.parse(entry.startDateTime),
        end: Date.parse(entry.endDateTime),
      })
    ) {
      return {
        activeRateEntry: entry,
        activeRateEntryIndex: index,
      };
    }
  }

  return {
    activeRateEntry: undefined,
    activeRateEntryIndex: -1,
  };
};

/**
 * Zone ids the saved rate holds rates for that the service method definition does not list.
 *
 * The rate sheet builds its columns from the definition's zones (`RateTableUploadForm.tsx:234`),
 * and `convertIdToNameRates` maps every other id to the same `undefined` key, so those rates have
 * no column to appear in. They stay in the form store and are submitted unchanged, which makes a
 * fully rated rate look unrated. Happens when the definition in use is not the one the rate was
 * written against: a rate saved before origin resolution existed, a contract whose origin could not
 * be read, or a service method the origin no longer serves.
 */
export const findZoneIdsMissingFromDefinition = (
  rateCalendar: PartnerRateCalendarEntry[] | undefined,
  carrierServiceMethod: CarrierServiceMethodInfo | undefined,
): string[] => {
  if (!rateCalendar?.length || !carrierServiceMethod) return [];

  const definitionZoneIds = new Set((carrierServiceMethod.zones ?? []).map((zone) => zone.zoneId));
  const missing = new Set<string>();

  const collect = (zoneIds: string[]) => {
    for (const zoneId of zoneIds) {
      if (!definitionZoneIds.has(zoneId)) missing.add(zoneId);
    }
  };

  for (const entry of rateCalendar) {
    const rateTables = [entry.rateTable, ...(entry.additionalRateTables ?? []).map(({ rateTable }) => rateTable)];

    for (const rateTable of rateTables) {
      // `rates` and `cubicRates` are keyed row then zone.
      for (const ratesByRow of [rateTable?.rates, rateTable?.cubicRates]) {
        for (const zoneRates of Object.values(ratesByRow ?? {})) {
          // The type says every row is an object; a null row from the API must not crash the caller.
          collect(Object.keys(zoneRates ?? {}));
        }
      }

      // These three are keyed by zone directly, with no row above them.
      for (const ratesByZone of [
        rateTable?.overSizedRate,
        rateTable?.ratePerWeightUnit,
        rateTable?.minimumOverWeightRate,
      ]) {
        collect(Object.keys(ratesByZone ?? {}));
      }
    }
  }

  return [...missing];
};
